import { ImageBackground, useWindowDimensions, Platform } from "react-native";
import { NativeModules, Pressable, StyleSheet, View } from "react-native";
import React, { useEffect, useState, useRef } from "react";

// 条件导入 FastImage（仅在 React Native 上使用）
let FastImage: any = null;
let OnLoadEvent: any = null;
let OnProgressEvent: any = null;

if (Platform.OS !== 'web') {
  const FastImageImport = require('react-native-fast-image');
  FastImage = FastImageImport.default;
  OnLoadEvent = FastImageImport.OnLoadEvent;
  OnProgressEvent = FastImageImport.OnProgressEvent;
}
import {
  uploadImageManager,
  UploadImageManagerCallback,
} from "../../../utils/uploadImage";
import { AnimatedCircularProgress } from "react-native-circular-progress";
import { white } from "../../../assets/style/colors";
import { ContentRenderProps } from "../ContentRender";
import { getImageShowSize } from "../../../utils/CommonUtils";
import * as ImSdk from "../../../../imsdk/api";
import { upsetMsg } from "../../../store/ChatStore";
import { fixRemoteUrl } from "../../../../imsdk/api";
import { getUid } from "../../../store/token";
import MediaPlaceholder from "../../../components/MediaPlaceholder";

export enum LoadState {
  LoadStart,
  Loading,
  LoadEnd,
  LoadError,
}
export type PicOrVideoRenderState = {
  size: number[];
  progress: number; // 上传进度（0 - 1）
  loadState: LoadState;
  loadProgress: number; // 加载进度（0 - 1）
  loaclLoadError: boolean;
  opacity: number;
  uploading: boolean;
};

const ImageRender = (props: ContentRenderProps) => {
  const { width: screenWidth } = useWindowDimensions();
  const metaW = Number(props.item.meta.width ?? "0");
  const metaH = Number(props.item.meta.height ?? "0");
  const showSize = getImageShowSize(metaW, metaH, screenWidth / 2); 
  const content = fixRemoteUrl(props.item.content);
  const dimensImageSize = (screenWidth / screenWidth / 2) * 160;
  const [state, setState] = useState<PicOrVideoRenderState>({
    size: [
      showSize.width > 0 ? showSize.width : dimensImageSize,
      showSize.height ? showSize.height : dimensImageSize,
    ],
    progress: 0,
    loadProgress: 0,
    loadState: LoadState.LoadStart,
    loaclLoadError: false,
    opacity: 1,
    uploading: props.item.upload_statue === "0",
  });

  const [retryCount, setRetryCount] = useState(0);

  const loadEndRef = useRef(false);
  const loadEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastProgressRef = useRef<number>(0);
  const progressTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 清理定时器：组件卸载时清理所有待处理的定时器（防止内存泄漏）
  useEffect(() => {
    return () => {
      // 清理进度加载防抖定时器
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
        progressTimeoutRef.current = null;
      }
      // 清理 handleLoadEnd 的延迟重置定时器
      if (loadEndTimeoutRef.current) {
        clearTimeout(loadEndTimeoutRef.current);
        loadEndTimeoutRef.current = null;
      }
    };
  }, []);

  // 获取上传进度（只读，不会触发循环）
  useEffect(() => {
    const key = `${props.item.sender}-${props.item.cid}-${props.item.upload_path}`;
    const progress = uploadImageManager.getProgress(key);
    if (progress > 0) {
      setState((prevState) => {
        if (prevState.progress !== progress) {
          return { ...prevState, progress };
        }
        return prevState;
      });
    }
  }, [props.item.sender, props.item.cid, props.item.upload_path]);

  // 上传进度监听
  useEffect(() => {
    let listener: UploadImageManagerCallback;
    const path = props.item.upload_path || "";
    const key = `${props.item.sender}-${props.item.cid}-${path}`;
    const isUpload = props.item.upload_statue === "0";

    if (isUpload) {
      listener = {
        onUploadProgress(progress) {
          setState((prevState) => {
            if (Math.abs(prevState.progress - progress) >= 1) {
              return { ...prevState, progress: progress };
            }
            return prevState;
          });
        },
        onSuccess() {
          setState((prevState) => {
            if (prevState.progress !== 100 || prevState.opacity !== 0 || prevState.uploading !== false) {
              return { ...prevState, progress: 100, opacity: 0, uploading: false };
            }
            return prevState;
          });
        },
        onFail() {
          setState((prevState) => {
            if (prevState.progress !== 100 || prevState.opacity !== 0 || prevState.uploading !== false) {
              return { ...prevState, progress: 100, opacity: 0, uploading: false };
            }
            return prevState;
          });
        },
      };
      uploadImageManager.addListener(key, listener);
    }

    return () => {
      if (isUpload && listener) {
        uploadImageManager.removeListener(key);
      }
    };
  }, [props.item.sender, props.item.cid, props.item.upload_path, props.item.upload_statue]);

  const handleLoadEnd = () => {
    // 使用 ref 防止重复调用
    if (!loadEndRef.current) {
      loadEndRef.current = true;
      setState((prevState) => {
        // 只有状态不同时才更新，避免不必要的重新渲染
        if (prevState.loadState !== LoadState.LoadEnd) {
          return { ...prevState, loadState: LoadState.LoadEnd };
        }
        return prevState;
      });

      // 延迟重置，避免立即再次触发
      // 清理之前的定时器，防止内存泄漏
      if (loadEndTimeoutRef.current) {
        clearTimeout(loadEndTimeoutRef.current);
      }
      loadEndTimeoutRef.current = setTimeout(() => {
        loadEndRef.current = false;
      }, 100);
    }
  };

  const onLoadStart = () => {
    setState((prevState) => {
      if (prevState.loadState !== LoadState.LoadStart) {
        return { ...prevState, loadState: LoadState.LoadStart };
      }
      return prevState;
    });
  };

  const handleLoadError = () => {
    //无论成功或失败都会onLoadEnd,
    setState((prevState) => {
      if (prevState.loadState !== LoadState.LoadError) {
        return { ...prevState, loadState: LoadState.LoadError };
      }
      return prevState;
    });
  };

  const handleLoaclLoadError = () => {
    //无论成功或失败都会onLoadEnd,
    setState((prevState) => {
      if (!prevState.loaclLoadError) {
        return { ...prevState, loaclLoadError: true };
      }
      return prevState;
    });
  };

  const renderImage = () => {
    // web 平台使用原生 Image，React Native 使用 FastImage
    if (Platform.OS === 'web') {
      return (
        <img
          key={`${content}_${retryCount}`}
          src={content}
          onError={handleLoadError}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: 4,
            objectFit: 'cover',
          } as any}
        />
      );
    }
    return (
      <FastImage
        key={`${content}_${retryCount}`}
        onLoad={onLoad}
        source={{
          uri: content,
          priority: FastImage.priority.normal,
          cache: FastImage.cacheControl.cacheOnly,
        }}
        onLoadEnd={handleLoadEnd}
        onLoadStart={onLoadStart}
        onError={handleLoadError}
        onProgress={onProgress}
        style={{ ...StyleSheet.absoluteFillObject, flex: 1, borderRadius: 4 }}
        resizeMode={"cover"}
      />
    );
  };

  const renderLocalImage = () => {
    // web 平台使用原生 img，React Native 使用 FastImage
    if (Platform.OS === 'web') {
      return (
        <img
          src={props.item.upload_path}
          onError={handleLoaclLoadError}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: 4,
            objectFit: 'cover',
          } as any}
        />
      );
    }
    return (
      <FastImage
        onLoad={onLoad}
        onError={handleLoaclLoadError}
        style={{ ...StyleSheet.absoluteFillObject, borderRadius: 4 }}
        source={{
          uri: props.item.upload_path,
          priority: FastImage.priority.normal,
          cache: FastImage.cacheControl.cacheOnly,
        }}
      />
    );
  };

  const onLoad = (event?: any) => { };
  const handleLoadWeb = (event: any) => { };
  const handleClick = () => {
    props.onClick?.();
  };

  const onProgress = (event: any) => {
    // web 平台不支持 onProgress，跳过此方法
    if (Platform.OS === 'web') return;

    const loaded = event.nativeEvent.loaded || 0;
    const total = event.nativeEvent.total || 1;
    const progress = (loaded / total) * 100;

    // 只有进度变化超过 1% 时才更新，减少更新频率
    if (Math.abs(progress - lastProgressRef.current) < 1) {
      return;
    }

    lastProgressRef.current = progress;

    // 清除之前的定时器
    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
    }

    // 使用防抖，延迟更新状态（150ms 延迟可减少频繁 setState 导致的内存压力）
    progressTimeoutRef.current = setTimeout(() => {
      setState((prevState) => {
        const newProgress = Math.round(progress);
        if (Math.abs(prevState.loadProgress - newProgress) >= 1 || prevState.loadState !== LoadState.Loading) {
          return {
            ...prevState,
            loadProgress: newProgress,
            loadState: LoadState.Loading,
          };
        }
        return prevState;
      });
    }, 150); // 150ms 防抖（优化内存使用）
  };

  // console.log(`\n ---->localPath: ${props.item.upload_path}  \n ---->remoteUrl: ${content}} \n`);
  const handleRetry = () => {
    if (state.loadState !== LoadState.LoadEnd && state.loadState !== LoadState.LoadError) {
      return; // 如果正在加载或者没有错误，不执行重试
    }

    setRetryCount(prev => {
      if (prev >= 3) {
        return 0; // 重置重试次数
      }
      return prev + 1;
    });
  }

  return (
    <ImageBackground
      source={require('../../../assets/image/ic_image_placeholder.png')}
      imageStyle={{ resizeMode: 'center', backgroundColor: '#666666', borderRadius: 4 }}
      style={[showSize, { justifyContent: 'center', alignItems: 'center', borderRadius: 4, overflow: 'hidden' }]}>
      <Pressable
        onPress={handleClick}
        onLongPress={props.onLongPress}
        disabled={props.talkSelectStatus === true || props.disabled == true}
        style={[showSize, { justifyContent: 'center', alignItems: 'center', borderRadius: 4, overflow: 'hidden' }]}
      >
        {((props.item.upload_path?.length ?? 0) == 0 || state.loaclLoadError) ? renderImage() : null}
        {props.item.upload_path && props.item.upload_path.length > 0 ? renderLocalImage() : null}

        {state.loadState === LoadState.LoadError && (
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: showSize.width,
            height: showSize.height,
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <MediaPlaceholder
              type={'image'}
              status={'error'}
              width={showSize.width}
              height={showSize.height}
              marginVertical={0}
              showReroad={false}
              onPress={handleRetry} />
          </View>
        )}

        {(!(props.item.upload_path && props.item.upload_path.length > 0) ||
          state.loaclLoadError) &&
          state.loadState === LoadState.Loading ? (
          <View style={[styles.overlay]}>
            <AnimatedCircularProgress
              size={40} // 圆的直径
              width={4} // 圆环的宽度
              rotation={0} // 旋转角度（0 表示从顶部开始）
              fill={state.loadProgress} // 填充的百分比
              duration={1} // 动画持续时间
              tintColor={white}
            />
          </View>
        ) : null}
        {state.uploading && props.item.send_statue !== "1" ? (
          <View style={[styles.overlay, { opacity: state.opacity }]}>
            <AnimatedCircularProgress
              size={80} // 圆的直径
              width={8} // 圆环的宽度
              rotation={0} // 旋转角度（0 表示从顶部开始）
              fill={state.progress} // 填充的百分比
              // backgroundColor="#e0e0e0" // 背景条颜色
              duration={1} // 动画持续时间
              tintColor={white}
            />
          </View>
        ) : null}
      </Pressable>
    </ImageBackground>
  );
};
export const ImageContentRender = React.memo(
  ImageRender,
  (prevProps, nextProps) => {
    // 使用更精确的比较，避免对象引用比较导致的无限循环
    return (
      prevProps.item.mid === nextProps.item.mid &&
      prevProps.item.content === nextProps.item.content &&
      prevProps.item.upload_path === nextProps.item.upload_path &&
      prevProps.item.upload_statue === nextProps.item.upload_statue &&
      prevProps.item.sender === nextProps.item.sender &&
      prevProps.item.cid === nextProps.item.cid &&
      prevProps.talkSelectStatus === nextProps.talkSelectStatus &&
      prevProps.disabled === nextProps.disabled
    );
  }
);
const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "center",
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.6)", // 半透明黑色背景
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
  },
  progressText: {
    color: "#fff",
    marginTop: 8,
    fontSize: 14,
  },
});
